import type { EvalLabelsByUser } from 'features/evaluations/metadata/utils/evalEventIndex'
import type { MetadataUserEditLoadOptions } from 'features/knowledgeDocumentViewer/utils/documentEntryLookups'
import { RolodexEntryType } from 'types'

export function evalEventsKey(
  url: string,
  options: MetadataUserEditLoadOptions
): [string, string, string] {
  return ['metadata-eval-events', url, JSON.stringify(options)]
}

export function evalOrgMetricsKey(
  url: string,
  organizationId: string,
  sites: readonly { id: string; name: string }[]
): [string, string, string, string, string] {
  return [
    'metadata-eval-org',
    url,
    organizationId,
    JSON.stringify(sites),
    'core-v5',
  ]
}

export function evalLabelsRevision(byUser: EvalLabelsByUser): string {
  let count = 0
  let latest = 0
  let mix = 0
  for (const pages of byUser.values()) {
    for (const [pageKey, label] of pages) {
      count += 1
      if (label.time > latest) latest = label.time
      mix ^= label.time
      for (let index = 0; index < pageKey.length; index += 1) {
        mix = (mix + pageKey.charCodeAt(index)) | 0
      }
    }
  }
  return `${count}:${latest}:${mix >>> 0}`
}

export function evalPipelineAgreementKey(
  url: string,
  organizationId: string,
  sites: readonly { id: string }[],
  labelsRevision: string
): [string, string, string, string, string] {
  return [
    'metadata-eval-pipeline',
    url,
    organizationId,
    sites.map((site) => site.id).join(','),
    labelsRevision,
  ]
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

export function isPageMetadataCacheKey(
  key: unknown,
  url: string,
  siteId: string,
  organizationId: string
): boolean {
  if (!Array.isArray(key)) return false
  if (key[0] === 'metadata-eval-active-fields')
    return key[1] === url && Array.isArray(key[2]) && key[2].includes(siteId)
  if (key[0] === 'metadata-eval-org' || key[0] === 'metadata-eval-pipeline')
    return key[1] === url && key[2] === organizationId
  const isEval = key[0] === 'metadata-eval-events' && key[1] === url
  const isRolodex =
    key[0] === `${url}/api/v2/entries/load` ||
    key[0] === `${url}/api/v2/entries/load/count`
  if (!isEval && !isRolodex) return false
  const body: unknown = isEval ? key[2] : key[1]
  if (typeof body !== 'string') return false
  try {
    const options: unknown = JSON.parse(body)
    if (!isRecord(options) || !isRecord(options.tags)) return false
    const types = options.type
    if (
      types !== RolodexEntryType.DOCUMENT_PAGE_METADATA_USER_EDIT &&
      (!Array.isArray(types) ||
        !types.includes(RolodexEntryType.DOCUMENT_PAGE_METADATA_USER_EDIT))
    )
      return false
    const site: unknown = options.tags.site
    const org: unknown = options.tags.organization
    if (site !== undefined)
      return site === siteId || (Array.isArray(site) && site.includes(siteId))
    return org === organizationId
  } catch {
    return false
  }
}
